import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NEVER_QUEUED, sendBillingMutation } from '@efacture/web-billing/lib/billing-mutations';
import { invoiceKeys, type CreateInvoiceInput, type Invoice, type PriceMode } from '@efacture/web-billing/api/queries/invoices';
import { posKeys } from '@efacture/web-billing/api/queries/pos';
import type { BillingTransport } from '@efacture/web-billing/billing-host/types';
import { useBillingApi } from '@efacture/web-billing/billing-host/api';

// Draft proposals (#241): a file read on « Facture depuis un fichier » is
// registered by its name and the SHA-256 of its bytes, never its content,
// then validated once into an invoice draft, a quote or a POS cart. Every
// write is online only: a queued replay could not be told apart from a
// second validation. The quote output is in draft-proposal-quote.ts.

export interface DraftProposal {
  id: string;
  fileName: string;
  contentSha256: string;
  status: 'OPEN' | 'USED';
  createdAt: string;
}

/** The file a document was created from. */
export interface DraftProposalSource {
  proposalId: string;
  fileName: string;
  contentSha256: string;
  readAt: string;
  usedAt: string | null;
}

export type DraftProposalDocumentKind = 'invoice' | 'quote';

export interface PosCartFromProposalInput {
  invoiceType?: string;
  priceMode: PriceMode;
  lines: { articleId: string; quantity: number }[];
}

export const draftProposalKeys = {
  all: ['draft-proposals'] as const,
  source: (kind: DraftProposalDocumentKind, id: string) => [...draftProposalKeys.all, 'source', kind, id] as const,
};

export function registerDraftProposal(api: BillingTransport, input: { fileName: string; contentSha256: string }): Promise<DraftProposal> {
  return sendBillingMutation<{ proposal: DraftProposal }>(api, 'POST', '/draft-proposals', input).then(
    (data) => data.proposal
  );
}

/** POST /api/draft-proposals/:id/invoice — the POST /api/invoices body. */
export function useCreateInvoiceFromProposal() {
  const api = useBillingApi();
  const queryClient = useQueryClient();
  return useMutation({
    ...NEVER_QUEUED,
    mutationFn: ({
      proposalId,
      input,
      idempotencyKey,
    }: {
      proposalId: string;
      input: CreateInvoiceInput;
      idempotencyKey: string;
    }) =>
      sendBillingMutation<Invoice>(api, 'POST', `/draft-proposals/${proposalId}/invoice`, input, { idempotencyKey }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.statistics() });
    },
  });
}

/** POST /api/draft-proposals/:id/pos-cart — articles and quantities; the server prices each line. */
export function useCreatePosCartFromProposal() {
  const api = useBillingApi();
  const queryClient = useQueryClient();
  return useMutation({
    ...NEVER_QUEUED,
    mutationFn: ({
      proposalId,
      input,
      idempotencyKey,
    }: {
      proposalId: string;
      input: PosCartFromProposalInput;
      idempotencyKey: string;
    }) =>
      sendBillingMutation<{ transaction: { id: string } }>(
        api, 'POST',
        `/draft-proposals/${proposalId}/pos-cart`,
        input,
        { idempotencyKey }
      ).then((data) => data.transaction),
    // Dropped, not invalidated: the POS adopts a cart on its first render of
    // the session, and a cached session would hand it an older cart first.
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: posKeys.activeSession() });
    },
  });
}

/** The file an invoice or a quote was created from, null when it was created otherwise. */
export function useDocumentSourceFile(kind: DraftProposalDocumentKind, id: string | undefined) {
  const api = useBillingApi();
  return useQuery({
    queryKey: draftProposalKeys.source(kind, id ?? ''),
    queryFn: () =>
      api
        .get<{ source: DraftProposalSource | null }>(`/draft-proposals/source/${kind}/${id}`)
        .then((data) => data.source),
    enabled: Boolean(id),
    staleTime: Infinity,
  });
}
